// withdrawalHelper.ts

import { ErrorType, FormDataType } from "app/types/fundingTypes";

// Create an object to hold the state of the form inputs
export const configureInitialFormData = (
  bankFields: string[],
  intermediaryBankFields: string[]
) => {
  const fields = [...bankFields, ...intermediaryBankFields];
  const initialFormData = fields.reduce((acc, field) => {
    acc[field] = "";
    return acc;
  }, {} as FormDataType);
  return initialFormData;
};

export const validateRequiredFields = (
  requiredFields: string[],
  formData: FormDataType,
  intl: { formatMessage: (arg0: { id: string }) => string }
) => {
  const requiredFieldsErrors: ErrorType[] = [];

  requiredFields.forEach(field => {
    const error: ErrorType | null = null;
    if (formData[field] == null || formData[field] === "") {
      requiredFieldsErrors.push({
        errorField: field,
        value: intl.formatMessage({ id: "Required" }),
      });
    } else {
      requiredFieldsErrors.push({
        errorField: field,
        value: "",
      });
    }
    error && requiredFieldsErrors.push(error);
  });

  return requiredFieldsErrors;
};

export const validateIBANOrAccountNumber = (
  formData: FormDataType,
  intl: { formatMessage: (arg0: { id: string }) => string }
) => {
  const ibanErrors: ErrorType[] = [];

  if (formData["iban"] === "" && formData["accountNumber"] === "") {
    const errorMessage = intl.formatMessage({
      id: "iban-or-account-number-required",
    });
    ibanErrors.push(
      {
        errorField: "iban",
        value: errorMessage,
      },
      {
        errorField: "accountNumber",
        value: errorMessage,
      }
    );
  }

  return ibanErrors;
};

export const validateAccountNumberAndSortCode = (
  formData: FormDataType,
  intl: { formatMessage: (arg0: { id: string }) => string }
) => {
  const errors: ErrorType[] = [];
  if (
    (formData["accountNumber"] !== "" && formData["sortCode"] === "") ||
    (formData["accountNumber"] === "" && formData["sortCode"] !== "")
  ) {
    const errorMessage = intl.formatMessage({
      id: "sort-code-required-account-number",
    });
    errors.push({
      errorField: "sortCode",
      value: errorMessage,
    });
  } else {
    errors.push({
      errorField: "sortCode",
      value: "",
    });
  }

  return errors;
};

export const getIntermediaryBankDetails = (formData: FormDataType) => {
  // Only send intermediate bank data when at least one of the fields is filled.
  // Otherwise the response will return errors based on the missing fields
  // If any intermediary bank fields are set, then add all to the request
  // show the api validation error
  const {
    intermediaryBankName,
    intermediaryBankAddress,
    intermediarySwiftCode,
  } = formData;
  const intermediateBankData = {
    bankName: intermediaryBankName,
    bankAddress: intermediaryBankAddress,
    swiftCode: intermediarySwiftCode,
  };

  // Check none of the intermediary bank fields are set, return null
  const isAnyIntermediaryFieldSet =
    intermediaryBankName !== "" ||
    intermediaryBankAddress !== "" ||
    intermediarySwiftCode !== "";

  return isAnyIntermediaryFieldSet ? intermediateBankData : null;
};

export const getBeneficiaryBankDetails = (formData: FormDataType) => {
  const {
    bankName,
    bankAddress,
    iban,
    accountNumber,
    sortCode,
    swiftCode,
    otherCode,
    otherNotes,
  } = formData;

  return {
    bankName,
    bankAddress,
    iban,
    accountNumber,
    sortCode,
    swiftCode,
    otherCode,
    otherNotes,
  };
};

export const checkIbanOrAccountNumberSet = (
  id: string,
  value: string,
  formData: FormDataType,
  setErrors: Function,
  intl
) => {
  // Rules:
  // Both iban and account number can be set at the same time
  // but only one needs to be set, as follows:
  // if iban is not set then account number and sort code must be set
  // If account number is set then sort code must be set
  // If sort code is set then account number must be set
  // if account number and sort code are not set, then iban must be set

  const currentFormData = { ...formData };
  const ibanErrorMessage = intl.formatMessage({
    id: "iban-or-account-number-required",
  });
  const sortCodeErrorMessage = intl.formatMessage({
    id: "sort-code-required-account-number",
  });
  // when IBAN blurs check if account number is set
  // if not show error message on both
  if (
    id === "iban" &&
    value === "" &&
    currentFormData["accountNumber"] === ""
  ) {
    setErrors([
      {
        errorField: "iban",
        value: ibanErrorMessage,
      },
      {
        errorField: "accountNumber",
        value: ibanErrorMessage,
      },
    ]);
  } else if (id === "iban" && value !== "") {
    // means iban is set so clear errors
    setErrors([
      {
        errorField: "iban",
        value: "",
      },
      {
        errorField: "accountNumber",
        value: "",
      },
      {
        errorField: "sortCode",
        value: "",
      },
    ]);
  }
  // accountNumber blur and iban not set
  if (
    id === "accountNumber" &&
    value === "" &&
    currentFormData["iban"] === ""
  ) {
    setErrors([
      {
        errorField: "iban",
        value: ibanErrorMessage,
      },
      {
        errorField: "accountNumber",
        value: ibanErrorMessage,
      },
    ]);
  } else if (id === "accountNumber" && value !== "") {
    // means accountNumber is set so clear errors
    setErrors([
      {
        errorField: "iban",
        value: "",
      },
      {
        errorField: "accountNumber",
        value: "",
      },
    ]);
  }
  // sortCode blur, sortCode not set and accountNumber set
  if (
    id === "sortCode" &&
    value === "" &&
    currentFormData["accountNumber"] !== ""
  ) {
    setErrors([
      {
        errorField: "sortCode",
        value: sortCodeErrorMessage,
      },
    ]);
  } else if (id === "sortCode" && value !== "") {
    // means sortCode is set so clear error
    setErrors([
      {
        errorField: "sortCode",
        value: "",
      },
    ]);
  }
  // sortCode or accountNumberblur, one set and the other not
  if (
    (id === "sortCode" || id === "accountNumber") &&
    value === "" &&
    currentFormData["accountNumber"] !== ""
  ) {
    setErrors([
      {
        errorField: "sortCode",
        value: sortCodeErrorMessage,
      },
    ]);
  } else if (id === "sortCode" && value !== "") {
    // means sortCode is set so clear error
    setErrors([
      {
        errorField: "sortCode",
        value: "",
      },
    ]);
  }
};
